import { existsSync, readFileSync } from 'node:fs';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import { Parser, Language, Query, LANGUAGE_VERSION, MIN_COMPATIBLE_VERSION } from 'web-tree-sitter';
import { getTreeSitterAssetsDir } from '../tree-sitter-assets-dir.js';
import { getLanguageSpec } from '../language-registry.js';

export type LoadedLanguage = {
  language: Language;
  query: Query;
};

let initPromise: Promise<void> | null = null;
const loadedByKey = new Map<string, LoadedLanguage>();
const failedLanguageKeys = new Set<string>();
let runtimeChain: Promise<unknown> = Promise.resolve();

export function withTreeSitterRuntimeLock<T>(fn: () => T | Promise<T>): Promise<T> {
  const next = runtimeChain.then(() => Promise.resolve(fn()));
  runtimeChain = next.then(
    () => undefined,
    () => undefined,
  );
  return next;
}

export async function ensureParserInit(): Promise<void> {
  if (!initPromise) {
    const assetsDir = getTreeSitterAssetsDir();
    initPromise = Parser.init({
      locateFile: (scriptName: string) => join(assetsDir, scriptName),
    }).catch((e) => {
      initPromise = null;
      throw e;
    });
  }
  await initPromise;
}

function resolveWasmPath(assetsDir: string, wasmFile: string): string {
  const candidates = [
    join(assetsDir, wasmFile),
    join(assetsDir, '..', '..', 'dist', 'tree-sitter', wasmFile),
    join(assetsDir, '..', '..', '..', 'node_modules', wasmFile.replace('.wasm', ''), wasmFile),
  ];
  const hit = candidates.find((p) => existsSync(p));
  if (!hit) throw new Error(`Missing tree-sitter wasm: ${wasmFile}`);
  return hit;
}

const CODE_NAV_QUERIES_ROOT = join(dirname(fileURLToPath(import.meta.url)), '..', 'queries');

function resolveTagsPath(assetsDir: string, tagsQueryFile: string): string {
  const pkgName = tagsQueryFile.split('/')[0] ?? '';
  const candidates = [
    join(CODE_NAV_QUERIES_ROOT, tagsQueryFile),
    join(assetsDir, tagsQueryFile),
    join(assetsDir, '..', '..', 'dist', 'tree-sitter', tagsQueryFile),
    join(assetsDir, '..', '..', '..', 'node_modules', pkgName, 'queries', 'tags.scm'),
  ];
  const hit = candidates.find((p) => existsSync(p));
  if (!hit) throw new Error(`Missing tags query: ${tagsQueryFile}`);
  return hit;
}

export async function loadLanguage(languageKey: string): Promise<LoadedLanguage | null> {
  const spec = getLanguageSpec(languageKey);
  if (!spec) return null;
  if (failedLanguageKeys.has(languageKey)) return null;
  const cached = loadedByKey.get(languageKey);
  if (cached) return cached;
  await ensureParserInit();
  const assetsDir = getTreeSitterAssetsDir();
  const language = await Language.load(resolveWasmPath(assetsDir, spec.wasmFile));
  const abiVersion = language.abiVersion;
  if (abiVersion < MIN_COMPATIBLE_VERSION || abiVersion > LANGUAGE_VERSION) {
    failedLanguageKeys.add(languageKey);
    throw new Error(
      `Incompatible tree-sitter ABI for ${languageKey}: grammar=${abiVersion}, runtime=${MIN_COMPATIBLE_VERSION}-${LANGUAGE_VERSION}`,
    );
  }
  const tagsSource = readFileSync(resolveTagsPath(assetsDir, spec.tagsQueryFile), 'utf8');
  const query = new Query(language, tagsSource);
  const loaded = { language, query };
  loadedByKey.set(languageKey, loaded);
  return loaded;
}
